import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { AxiosResponse } from 'axios'
import { IScheduledBreaksModalState } from 'pages/Breaks/ScheduledBreaksModal'
import { toast } from 'react-toastify'
import {
  IBreakTemplate,
  IGetBreakPoliciesSingleResponse,
  IGetAllScheduledBreaksResponse,
  IBreakPolicyPostRequest,
} from 'types/api/breaksApi.interface'
import { IPaginatedResponse } from 'types/api/general.interface'
import {
  BreakPolicyScopeType,
  breakPolicyEnforcementTypeOptions,
  IBreakPolicyState,
} from 'types/breakPolicy.interface'
import { baseUrl } from 'utils/axios'
import { getParams, handleErrors } from 'utils/helpers'

interface BreaksInitialState {
  isLoading: boolean
  templates: {
    data: IBreakTemplate[]
    totalPages: number
    totalCount: number
  }
  scheduledBreaks: IGetAllScheduledBreaksResponse
  breakPolicies: {
    data: IGetBreakPoliciesSingleResponse[]
    totalPages: number
    totalCount: number
  }
  singleBreakPolicy: IBreakPolicyState
}

export interface IParsedBreaks {
  user: {
    name: string
    id: number
  }
  breaks: {
    breakIndex: number
    scheduledStartTime: string
    scheduledEndTime: string
    breakDuration: number
  }[]
}

const initialState: BreaksInitialState = {
  isLoading: false,
  templates: {
    data: [],
    totalPages: null,
    totalCount: null,
  },
  scheduledBreaks: null,
  breakPolicies: {
    data: [],
    totalPages: null,
    totalCount: null,
  },
  singleBreakPolicy: null,
}

const mapBreakPolicyResponseToState = (data: IGetBreakPoliciesSingleResponse): IBreakPolicyState => ({
  id: String(data.id),
  breakTemplate: {
    value: data.breakTemplate.id,
    label: data.breakTemplate.name,
  },
  scopeType: {
    value: data.scopeType,
    label: data.scopeType === BreakPolicyScopeType.Site ? 'Site' : 'Company',
  },
  siteNode: data.siteNode
    ? { value: data.siteNode.id, label: data.siteNode.name }
    : null,
  maxOnBreak: data.maxOnBreak,
  minActive: data.minActive,
  minGapBetweenBreaksInMinutes: data.minGapBetweenBreaksInMinutes,
  validFromDate: data.validFromDate ? new Date(data.validFromDate) : null,
  validToDate: data.validToDate ? new Date(data.validToDate) : null,
  roleRules: (data.roleRules || []).map((rule) => ({
    id: rule.id,
    role: { value: rule.role.id, label: rule.role.name },
    minimumActiveCount: rule.minimumActiveCount,
  })),
  constraints: (data.constraints || []).map((constraint) => ({
    id: constraint.id,
    breakTemplateBreak: {
      value: constraint.breakTemplateBreak.id,
      label: `${constraint.breakTemplateBreak.durationMinutes} min.`,
    },
    index: constraint.index + 1,
    anchorType: {
      value: constraint.anchorType,
      label: constraint.anchorType === 1 ? 'Shift Start' : 'Shift End',
    },
    minOffsetMinutes: constraint.minOffsetMinutes,
    maxOffsetMinutes: constraint.maxOffsetMinutes,
    placementStrategyType: {
      value: constraint.placementStrategyType,
      label:
        constraint.placementStrategyType === 1
          ? 'Earliest'
          : constraint.placementStrategyType === 2
            ? 'Latest'
            : 'Midpoint',
    },
    durationOverrideMinutes: constraint.durationOverrideMinutes,
    enforcementType: breakPolicyEnforcementTypeOptions.find(
      (o) => o.value == constraint.enforcementType,
    ) || null,
  })),
})

const buildBreakPolicyRequest = (state: IBreakPolicyState): IBreakPolicyPostRequest => {
  const req: IBreakPolicyPostRequest = {
    breakTemplateId: Number(state.breakTemplate?.value),
    scopeType: state.scopeType?.value,
    maxOnBreak: Number(state.maxOnBreak),
    minActive: Number(state.minActive),
    minGapBetweenBreaksInMinutes: Number(state.minGapBetweenBreaksInMinutes),
    validFromDate: state.validFromDate?.toISOString(),
    validToDate: state.validToDate?.toISOString(),
    roleRules: (state.roleRules || []).map((rule) => ({
      roleId: Number(rule.role?.value),
      minimumActiveCount: Number(rule.minimumActiveCount),
    })),
    constraints: state.constraints.map((constraint) => ({
      breakTemplateBreakId: Number(constraint.breakTemplateBreak?.value),
      index: Number(constraint.index) - 1,
      anchorType: constraint.anchorType?.value,
      minOffsetMinutes: Number(constraint.minOffsetMinutes),
      maxOffsetMinutes: Number(constraint.maxOffsetMinutes),
      placementStrategyType: constraint.placementStrategyType?.value,
      durationOverrideMinutes: Number(constraint.durationOverrideMinutes),
      enforcementType: constraint.enforcementType?.value,
    })),
  }
  if (state.scopeType?.value === BreakPolicyScopeType.Site && state.siteNode?.value) {
    req.siteNodeId = Number(state.siteNode.value)
  }
  return req
}

export const editScheduledBreak = createAsyncThunk(
  'breaks/editScheduledBreak',
  async (state: IScheduledBreaksModalState, thunkAPI) => {
    try {
      await baseUrl.patch('break/scheduled/user', state)
      return state
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getBreakTemplates = createAsyncThunk(
  'breaks/getTemplates',
  async (params: any, thunkAPI) => {
    try {
      const resp: AxiosResponse<IPaginatedResponse<IBreakTemplate>> = await baseUrl.get(
        'break/template',
        {
          params,
        },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const addBreakTemplate = createAsyncThunk(
  'breaks/addBreakTemplate',
  async (state: IBreakTemplate, thunkAPI) => {
    try {
      await baseUrl.post('break/template', state)
      await thunkAPI.dispatch(getBreakTemplates(getParams({ location })))
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const editBreakTemplate = createAsyncThunk(
  'breaks/editBreakTemplate',
  async (state: IBreakTemplate, thunkAPI) => {
    try {
      await baseUrl.patch('break/template/' + state.id, state)
      await thunkAPI.dispatch(getBreakTemplates(getParams({ location })))
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const deleteBreakTemplate = createAsyncThunk(
  'breaks/deleteBreakTemplate',
  async (id: string, thunkAPI) => {
    try {
      await baseUrl.delete('break/template/' + id)
      await thunkAPI.dispatch(getBreakTemplates(getParams({ location })))
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getScheduledBreaks = createAsyncThunk(
  'breaks/getScheduledBreaks',
  async (params: any, thunkAPI) => {
    try {
      const resp: AxiosResponse<IBreakTemplate> = await baseUrl.get('break/scheduled', {
        params,
      })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getBreakPolicies = createAsyncThunk(
  'breaks/getBreakPolicies',
  async (params: any, thunkAPI) => {
    try {
      const resp: AxiosResponse<IPaginatedResponse<IGetBreakPoliciesSingleResponse>> =
        await baseUrl.get('break/policy', {
          params,
        })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getSingleBreakPolicy = createAsyncThunk(
  'breaks/getSingleBreakPolicy',
  async (id: string, thunkAPI) => {
    try {
      const resp: AxiosResponse<IGetBreakPoliciesSingleResponse> = await baseUrl.get(
        'break/policy/' + id,
      )
      return mapBreakPolicyResponseToState(resp.data)
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const deleteBreakPolicy = createAsyncThunk(
  'breaks/deleteBreakPolicy',
  async (id: string, thunkAPI) => {
    try {
      const resp: AxiosResponse<IGetBreakPoliciesSingleResponse> = await baseUrl.delete(
        'break/policy/' + id,
      )
      await thunkAPI.dispatch(getBreakPolicies(getParams({ location })))
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const addBreakPolicy = createAsyncThunk(
  'breaks/addBreakPolicy',
  async (state: IBreakPolicyState, thunkAPI) => {
    try {
      const req = buildBreakPolicyRequest(state)
      const resp: AxiosResponse<IGetBreakPoliciesSingleResponse> = await baseUrl.post(
        'break/policy/',
        req,
      )
      await thunkAPI.dispatch(getBreakPolicies(getParams({ location })))
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const editBreakPolicy = createAsyncThunk(
  'breaks/editBreakPolicy',
  async (state: IBreakPolicyState, thunkAPI) => {
    try {
      const req = buildBreakPolicyRequest(state)
      const resp: AxiosResponse<IGetBreakPoliciesSingleResponse> = await baseUrl.patch(
        'break/policy/' + state.id,
        req,
      )
      await thunkAPI.dispatch(getBreakPolicies(getParams({ location })))
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

const breaksSlice = createSlice({
  name: 'breaks',
  initialState,
  reducers: {
    clearBreakTemplates: (state) => {
      state.templates = {
        data: [],
        totalPages: null,
        totalCount: null,
      }
    },
    clearScheduledBreaks: (state) => {
      state.templates = {
        data: [],
        totalPages: null,
        totalCount: null,
      }
    },

    clearBreakPolicies: (state) => {
      state.breakPolicies = {
        data: [],
        totalPages: null,
        totalCount: null,
      }
    },
  },
  extraReducers: {
    [getBreakTemplates.pending.type]: (state) => {
      state.isLoading = true
    },
    [getBreakTemplates.fulfilled.type]: (
      state,
      { payload }: { payload: IPaginatedResponse<IBreakTemplate> },
    ) => {
      state.isLoading = false
      state.templates = payload
    },
    [getBreakTemplates.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [addBreakTemplate.pending.type]: (state) => {
      state.isLoading = true
    },
    [addBreakTemplate.fulfilled.type]: (state) => {
      toast.success('Break template saved successfully')
      state.isLoading = false
    },
    [addBreakTemplate.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [editBreakTemplate.pending.type]: (state) => {
      state.isLoading = true
    },
    [editBreakTemplate.fulfilled.type]: (state) => {
      toast.success('Break template updated successfully')
      state.isLoading = false
    },
    [editBreakTemplate.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [deleteBreakTemplate.pending.type]: (state) => {
      state.isLoading = true
    },
    [deleteBreakTemplate.fulfilled.type]: (state) => {
      toast.success('Break template deleted successfully')
      state.isLoading = false
    },
    [deleteBreakTemplate.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },

    [getScheduledBreaks.pending.type]: (state) => {
      state.isLoading = true
    },
    [getScheduledBreaks.fulfilled.type]: (
      state,
      { payload }: { payload: IGetAllScheduledBreaksResponse },
    ) => {
      state.isLoading = false
      state.scheduledBreaks = payload
    },
    [getScheduledBreaks.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },

    [getBreakPolicies.pending.type]: (state) => {
      state.isLoading = true
    },
    [getBreakPolicies.fulfilled.type]: (
      state,
      { payload }: { payload: IPaginatedResponse<IGetBreakPoliciesSingleResponse> },
    ) => {
      state.isLoading = false
      state.breakPolicies = payload
    },
    [getBreakPolicies.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getSingleBreakPolicy.pending.type]: (state) => {
      state.isLoading = true
    },
    [getSingleBreakPolicy.fulfilled.type]: (state, { payload }: { payload: IBreakPolicyState }) => {
      state.isLoading = false
      state.singleBreakPolicy = payload
    },
    [getSingleBreakPolicy.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [deleteBreakPolicy.pending.type]: (state) => {
      state.isLoading = true
    },
    [deleteBreakPolicy.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Break policy deleted successfully.')
    },
    [deleteBreakPolicy.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [addBreakPolicy.pending.type]: (state) => {
      state.isLoading = true
    },
    [addBreakPolicy.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Break policy saved successfully.')
    },
    [addBreakPolicy.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [editBreakPolicy.pending.type]: (state) => {
      state.isLoading = true
    },
    [editBreakPolicy.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Break policy updated successfully.')
    },
    [editBreakPolicy.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [editScheduledBreak.pending.type]: (state) => {
      state.isLoading = true
    },
    [editScheduledBreak.fulfilled.type]: (
      state,
      { payload }: { payload: IScheduledBreaksModalState },
    ) => {
      state.isLoading = false
      const { userId, breakIndex, newStartDate } = payload
      if (state.scheduledBreaks && Array.isArray(state.scheduledBreaks.breaks)) {
        state.scheduledBreaks.breaks.forEach((userBreak) => {
          if (userBreak.user.id === userId) {
            userBreak.breaks.forEach((br) => {
              if (br.breakIndex === breakIndex) {
                br.scheduledStartTime = newStartDate
              }
            })
          }
        })
      }
      toast.success('Break policy updated successfully.')
    },
    [editScheduledBreak.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
  },
})

export const { clearBreakTemplates, clearScheduledBreaks, clearBreakPolicies } = breaksSlice.actions

export default breaksSlice.reducer
